/**
 * LANGUAGE FLAG SELECTOR (v4.7.56)
 *
 * Unified accessible flag-based language picker.
 * Persists selection via lib/languageResolver:
 *   - localStorage `aureus_language` (canonical)
 *   - localStorage `aureus_language_explicit` = '1'
 *   - the signed-in account language, including users.pref_language
 *
 * Accessibility:
 *   - role="listbox" with arrow-key navigation
 *   - Esc closes, Enter selects
 *   - aria-expanded on trigger
 */
import React, { useEffect, useRef, useState, useCallback } from 'react'
import i18n from '../lib/i18n'
import { SUPPORTED_LANGUAGES, setStoredLanguage, persistLanguageToDb, isSupported } from '../lib/languageResolver'
import { ensureSessionHeaders } from '../lib/serverSession'

export interface LanguageFlagSelectorProps {
  variant?: 'header' | 'inline'
  authenticated?: boolean
  className?: string
  onChange?: (code: string) => void
}

export default function LanguageFlagSelector({ variant = 'header', authenticated = false, className = '', onChange }: LanguageFlagSelectorProps) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<string>(() => {
    const code = (i18n.language || 'en').split('-')[0]
    return isSupported(code) ? code : 'en'
  })
  const [busy, setBusy] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const onLangChange = (lng: string) => setActive(lng)
    i18n.on('languageChanged', onLangChange)
    return () => { i18n.off('languageChanged', onLangChange) }
  }, [])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey) }
  }, [])

  const select = useCallback(async (code: string) => {
    if (code === active || busy) { setOpen(false); return }
    setBusy(true)
    try {
      setStoredLanguage(code, { explicit: true })
      try { await i18n.changeLanguage(code) } catch (e) { console.warn('[LangSelector] i18n.changeLanguage failed:', e) }
      setActive(code)
      if (authenticated) {
        persistLanguageToDb(code, ensureSessionHeaders).catch(err => console.warn('[LangSelector] DB persist failed:', err))
      }
      onChange?.(code)
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }, [active, busy, authenticated, onChange])

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); setOpen(true)
      requestAnimationFrame(() => {
        const first = listRef.current?.querySelector<HTMLElement>('li[role="option"]')
        first?.focus()
      })
    }
  }

  const onItemKey = (e: React.KeyboardEvent, idx: number) => {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('li[role="option"]') || [])
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus() }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(SUPPORTED_LANGUAGES[idx].code) }
  }

  const current = SUPPORTED_LANGUAGES.find(l => l.code === active) || SUPPORTED_LANGUAGES[0]

  const isHeader = variant === 'header'
  const triggerStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: isHeader ? '8px 10px' : '6px 10px',
    background: isHeader ? '#1f2937' : 'transparent',
    color: '#FFD700',
    border: isHeader ? '1px solid #374151' : '1px solid #FFD700',
    borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 500,
    minHeight: 40, lineHeight: 1,
  }

  return (
    <div ref={rootRef} className={`aureus-lang-selector ${className}`} style={{ position: 'relative' }}>
      <button
        type="button" aria-haspopup="listbox" aria-expanded={open}
        aria-label={`Language: ${current.nativeName}. Click to change.`}
        onClick={() => setOpen(o => !o)} onKeyDown={onTriggerKey}
        disabled={busy} style={triggerStyle}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>{current.flag}</span>
        <span style={{ fontWeight: 600 }}>{current.code.toUpperCase()}</span>
        <span aria-hidden style={{ marginLeft: 2, opacity: 0.8 }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <ul
          ref={listRef} role="listbox" aria-label="Select language"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 1000,
            minWidth: 200, maxHeight: 360, overflowY: 'auto',
            background: '#111827', border: '1px solid #374151', borderRadius: 12,
            padding: 6, margin: 0, listStyle: 'none',
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang, idx) => {
            const selected = lang.code === active
            return (
              <li
                key={lang.code} role="option" aria-selected={selected} tabIndex={-1}
                onClick={() => select(lang.code)} onKeyDown={(e) => onItemKey(e, idx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  color: selected ? '#FFD700' : '#e5e7eb',
                  background: selected ? 'rgba(255,215,0,0.08)' : 'transparent',
                  outline: 'none',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = selected ? 'rgba(255,215,0,0.08)' : 'transparent' }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)' }}
                onBlur={(e) => { (e.currentTarget as HTMLElement).style.background = selected ? 'rgba(255,215,0,0.08)' : 'transparent' }}
              >
                <span style={{ fontSize: 20 }} aria-hidden>{lang.flag}</span>
                <span style={{ flex: 1 }}>{lang.nativeName}</span>
                <span style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase' }}>{lang.code}</span>
                {selected && <span aria-hidden style={{ color: '#FFD700' }}>✓</span>}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
